import React, { useEffect, useState } from 'react'
import Card from '../../../common/card/Card'
import Button from '../../../common/button/Button'
import { QueryClient, useQuery } from '@tanstack/react-query'
import { performRequest } from '../../../common/utils'
import { useParams } from 'react-router-dom'
import ResponseMessage from '../../../common/modal/ResponseMessage'
import Modal from '../../../common/modal/Modal'
import { useGroupDashboardContext } from '../GroupDashboardContext'
import CheckoutModal from './CheckoutModal'
import { isInteger } from './Utils'
import MicroModal from 'micromodal'

const AddTeamSeats = ({
  teamData = [],
  refetchTeamData,
  teamOptionsData = [],
  from = ''
}) => {

  const isSubscriptionActive = () => {
    return ['active', 'on-hold' ].includes( teamData.subscription.subscription_status )
  }
  const getTeamSeatsValue = () => {
    if ( teamData.product.teams_pricing === 'per_member' ) {
      return teamData.product.teams_min_member_count
    } else {
      return teamData.product.teams_max_member_count
    }
  }
  const getNumberInputStep = () => {
    return teamData.product.teams_pricing === 'per_member' ? 1 : teamData.product.teams_max_member_count
  }
  const { filtersData, setSelectedSeats } = useGroupDashboardContext()
  const [ seats, setSeats ] = useState( isSubscriptionActive() ? teamData.team_seat_count : getTeamSeatsValue() )
  const [ seatInvalid, setSeatInvalid ] = useState( false )
  const [ responseMessage, setResponseMessage ] = useState('')
  const modalId = 'msync-seat-checkout-modal'
  
  const params = useParams()
  const teamId = params.team_id
  
  const seatText = ( seats = 0 ) => {
    return seats > 1 ? 'seats' : 'seat'
  }
  const queryClient = new QueryClient()
  const {
    isLoading,
    isFetching,
    refetch,
    data } = useQuery({
      queryKey: ['tms_csv_import_add_seat'],
      queryFn: addTeamSeats,
      refetchOnWindowFocus: false,
      enabled: false
    }, queryClient)

    async function addTeamSeats(){
      let seat_count = seats
      if ( !isSubscriptionActive() && teamData.product.teams_pricing !== 'per_member' ) {
        seat_count = seats / teamData.product.teams_max_member_count
      }

      const formData = new FormData()
      formData.append('action', 'tms_csv_import_add_seat')
      formData.append('team_id', teamId)
      formData.append('seat_count', seat_count)
      formData.append('nonce', msyncCsvImport.nonce_create)

      try {
        const request = await performRequest(msyncCsvImport.url, formData)

        if( request.url !== msyncCsvImport.url){
          setResponseMessage('')
          setSelectedSeats({
            totalSeats: totalSeats,
            seatCount: teamData.team_seat_count,
            seatToAdd: seatToAdd,
            seatText: seatText( seats ),
            totalSeatsText: seatText( totalSeats ),
            checkoutURL: request.url.toString()
          })
          MicroModal.show(modalId)
          return true
        }

        const res = await request.json()
  
        if( ! res.success ){
          throw new Error( res.errors.shift() )
        }
        setResponseMessage('')
        refetchTeamData()
        return res.success
      } catch (error) {
        console.error(error)
        setResponseMessage({
          type:'error',
          message: error.toString()
        })
        return false
      }
    }

  const getNewSeatToAdd = ( value ) => {
    let seatText = Number( value ) ?? 1

    if( teamData.product.teams_pricing === 'per_team' ){
      seatText = Number( value )
    }

    if( isSubscriptionActive() ){
      const originalCount = Number( teamData.team_seat_count )
      let newSeatCount = seatText - originalCount
      newSeatCount = Math.abs( newSeatCount )
      seatText = newSeatCount
    }
    
    return seatText
  }

  const getNewTotalSeat = ( value ) => {
    let newTotalSeat = Number( value ) + Number( teamData.team_seat_count )
    
    if( teamData.product.teams_pricing === 'per_team' ){
      const maxMember = teamData.product.teams_max_member_count ?? 1
      newTotalSeat = Number( value ) + Number( teamData.team_seat_count )
    }

    if( isSubscriptionActive() ) newTotalSeat = Number( value )

    return newTotalSeat
  }

  useEffect(() => {
    const isSeatValid = ( ! seats || seats <= 0 || ! isInteger( seats ) || ( totalSeats < teamData.team_members_count ) ) 
      || seats < parseInt(teamData.product.teams_min_member_count) || ( teamData.product.teams_pricing === 'per_member' && seats > ( parseInt(teamData.product.teams_max_member_count) - teamData.team_seat_count ) )
    setSeatInvalid( isSeatValid )
  }, [ seats ])

  const [ totalSeats, setTotalSeats ] = useState( getNewTotalSeat( seats ) )
  const [ seatToAdd, setSeatToAdd ] = useState( getNewSeatToAdd( seats ) )

  const handleChangeSeatValue = ( value ) => {
    const newValue = value === '' ? '' : value
    setSeats( newValue )

    const newSeatToAdd = getNewSeatToAdd( newValue )
    setSeatToAdd( newSeatToAdd )
    const newTotalSeat = getNewTotalSeat( newValue )
    setTotalSeats( newTotalSeat )
  }

  const submitButton = () => {
    return(
      <Button
        name={ isLoading || isFetching ? 'Processing...' : 'Submit' }
        className='msync-primary-btn'
        onClick={ () => refetch() }
        disabled={ seatInvalid || isLoading || isFetching || seatToAdd === 0 ? true : false  }
      />
    )
  }

  const validateInput = ( seats ) => { 
    if( seats === '' || ! isInteger( seats ) ){
      return 'Please enter a valid seat value'
    } else if( teamData.product.teams_pricing === 'per_member' && ( ( seats === 0 || seats < 0 ) || ( parseInt(teamData.product.teams_max_member_count) - teamData.team_seat_count ) < 0 || ( parseInt(teamData.product.teams_max_member_count) - teamData.team_seat_count ) === 0 )) {
      return "You have reached the maximum seat count allowed by the product settings. ( max : " + teamData.product.teams_max_member_count + ' )'
    } else if ( seats < parseInt(teamData.product.teams_min_member_count) ) {
      return "The minimum seat(s) you can buy is " + teamData.product.teams_min_member_count
    } else if ( teamData.product.teams_pricing === 'per_member' && seats > ( parseInt(teamData.product.teams_max_member_count) - teamData.team_seat_count ) ) {
      return "Remaining seat(s): " + ( parseInt(teamData.product.teams_max_member_count) - teamData.team_seat_count ) + "."
    } else if( isSubscriptionActive() && ( teamData.team_seat_count === seats ) || seatToAdd === 0 ){
      return 'Seat count unchanged'
    }else if( totalSeats < teamData.team_members_count ){
      return `The value provided (${totalSeats}) is lower than the number of seats used (${teamData.team_members_count}), it should be equal or higher than the seats used.`
    }else if( seatInvalid && isSubscriptionActive() ){
      return (
        <span className='msync-message-error'> This {filtersData.woo.teams.singular_label.toLowerCase()} does not have enough unoccupied seats to remove this many seats. </span>
      )
    }else{
      return (
        <span> This action will <strong>{ totalSeats >= teamData.team_seat_count ? 'add' : 'remove' } { `${seatToAdd} ${seatText( seats )}` } </strong>, resulting in a total of <strong>{ `${totalSeats} ${seatText( totalSeats )}` }.</strong> </span>
      )
    }
  }

  const AddSeatsContent = () => {
    return (
      <div className='msync-add-team-seats-content'>
        <span>{
          isSubscriptionActive() ? 
          'Enter the new seat count to reduce seats or to purchase additional seats.' :
          'Enter the number of seats you would like to add to this team.'
        }</span>
        { responseMessage && ! isLoading && ! isFetching && <ResponseMessage response={ responseMessage } /> }
        <div className='msync-add-team-seats-content-form' >
          <input 
            className={`msync-input ${from === "manage-seats" ? "full-width" : ''}`}
            type="number"
            placeholder='Enter number of seats'
            value={ seats }
            min={ isSubscriptionActive() ? 1 : teamData.product.teams_min_member_count  }
            max={ teamData.product.teams_pricing === 'per_member' ? teamData.product.teams_max_member_count - teamData.team_seat_count : '' }
            step={ isSubscriptionActive() ? 1 : getNumberInputStep() }
            onChange={ (e) => handleChangeSeatValue( e.target.value ) }
            disabled={ teamData.product.teams_pricing === 'per_member' && ( seats === 0 || seats < 0 ) ? true : false }
          />
          { from !== 'manage-seats' && submitButton() }
        </div>
        { validateInput( seats ) }
      </div>
    )
  }

  return (
    <>
      {
        from === 'manage-seats' &&
        <Modal 
          modalId="msync-modal-manage-seats" 
          modalHeading={ filtersData.home?.manage_seats_nav_button_label }
          buttonTitle="" 
          modalClassNames="msync-modal-custom msync-modal-custom-sm"
        > 
          <main className="msync-modal__body msync-modal__container-sm">
            <div className='msync-modal-content-section'>
              { AddSeatsContent() }
            </div>
          </main>
          <footer className='msync-modal__footer'>
            <div>
              { submitButton() }
              <Button
                name='Cancel'
                onClick={()=>MicroModal.close('msync-modal-manage-seats')}
              />
            </div>
          </footer>
        </Modal>
      }
      {
        teamData.team_seat_count !== null && from === 'team-settings' &&
        <div className='msync-add-team-seats'>
          <Card title='Add Seats'>
              { AddSeatsContent() }
          </Card>
        </div>
      }
      { <CheckoutModal modalId={ modalId } responseMessage={ responseMessage } /> }
    </>
  )
}

export default AddTeamSeats
